import React, { useMemo } from 'react'
import { WebInputFile } from '@codeleap/types'
import { useCallback, useEffect, useState } from '@codeleap/common'
import { Crop } from 'react-image-crop'
import { ImageReading } from '@codeleap/web'
import { calculateProportionalDimensions, cropImage, readImage } from './utils'

export type UseCropPicker = {
  file: File
  aspect?: number
  minWidth?: number
  minHeight?: number
  onCrop?: (croppedFile: WebInputFile | null) => void
}

export function useCropPicker({
  aspect,
  minWidth: minW,
  minHeight: minH,
  file,
  onCrop
}: UseCropPicker) {
  const [image, setImage] = useState<ImageReading>(null)
  const [crop, setCrop] = useState<Crop>()
  const [relativeCrop, setRelativeCrop] = useState<Crop>()

  const [imageDimensions, setImageDimensions] = useState<{
    naturalWidth: number
    naturalHeight: number
    displayWidth: number
    displayHeight: number
    aspectRatio: number
  } | null>(null)

  const cleanup = () => {
    setRelativeCrop(null)
    setCrop(undefined)
    setImageDimensions(null)
    setTimeout(() => setImage(null), 500)
  }

  const handleConfirmCrop = async () => {
    const [preview, croppedBlob] = await cropImage(image, relativeCrop)

    const newImage: WebInputFile = {
      file: new File([croppedBlob], 'cropped.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      }),
      preview,
    }

    onCrop(newImage)

    setTimeout(() => cleanup())
  }

  const handleCancelCrop = () => {
    onCrop(null)

    setTimeout(() => cleanup())
  }

  const loadFile = useCallback(async (toReadFile: File) => {
    const imageData = await readImage(toReadFile)
    const { naturalWidth, naturalHeight } = imageData

    const dimensions = calculateProportionalDimensions(naturalWidth, naturalHeight)

    setImageDimensions(dimensions)

    const targetAspect = aspect || dimensions.aspectRatio
    const imageAspect = dimensions.aspectRatio

    let initialCrop: Crop

    if (aspect) {
      const v =
        imageAspect >= targetAspect
          ? {
            width: ((naturalHeight * targetAspect) / naturalWidth) * 100,
            height: 100,
          }
          : {
            width: 100,
            height: (naturalWidth / targetAspect / naturalHeight) * 100,
          }

      initialCrop = {
        ...v,
        x: (100 - v.width) / 2,
        y: (100 - v.height) / 2,
        unit: '%' as const,
      }
    } else {
      initialCrop = {
        width: 100,
        height: 100,
        x: 0,
        y: 0,
        unit: '%' as const,
      }
    }

    setCrop(initialCrop)
    setRelativeCrop(initialCrop)
    setImage(imageData)
  }, [])

  const getMinDimensions = useCallback(() => {
    if (!imageDimensions) return { minWidth: minW ?? 100, minHeight: minH ?? 100 }

    const targetAspect = aspect || imageDimensions.aspectRatio
    const calculatedMinWidth = (minW ?? 100) * targetAspect
    const calculatedMinHeight = minH ?? 100

    return {
      minWidth: Math.max(calculatedMinWidth, 50),
      minHeight: Math.max(calculatedMinHeight, 50),
    }
  }, [aspect, imageDimensions, minW, minH])

  const handleCropChange = useCallback((newCrop: Crop) => {
    if (!imageDimensions) {
      setCrop(newCrop)
      return
    }

    const { minWidth, minHeight } = getMinDimensions()

    setCrop({
      ...newCrop,
      width: newCrop.width < minWidth ? minWidth : newCrop.width,
      height: newCrop.height < minHeight ? minHeight : newCrop.height,
    })
  }, [imageDimensions, getMinDimensions])

  const containerStyles = useMemo(() => {
    if (!imageDimensions) return {}

    return {
      width: imageDimensions.displayWidth,
      height: imageDimensions.displayHeight,
      maxWidth: '90vw',
      maxHeight: '70vh',
      margin: '0 auto',
    }
  }, [imageDimensions])

  const imageStyles = useMemo(() => {
    if (!imageDimensions) return {}

    return {
      width: imageDimensions.displayWidth,
      height: imageDimensions.displayHeight,
      objectFit: 'contain' as const,
    }
  }, [imageDimensions])

  useEffect(() => {
    loadFile(file)
  }, [])

  return {
    containerStyles,
    imageStyles,
    handleCropChange,
    handleConfirmCrop,
    handleCancelCrop,
    setRelativeCrop,
    crop,
    image,
  }
}
